import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Sparkles, Loader2, Calendar, Mail, CheckCircle } from "lucide-react"
import type { LeadFormData } from "@/types"

interface LeadCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: LeadFormData) => void
  sessionDuration: number
  messageCount: number
}

export function LeadCaptureModal({
  isOpen,
  onClose,
  onSubmit,
  sessionDuration,
  messageCount
}: LeadCaptureModalProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [formData, setFormData] = useState<LeadFormData>({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    consent: false
  })
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({})

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = t("leadCapture.errors.nameRequired")
    }

    if (!formData.email.trim()) {
      newErrors.email = t("leadCapture.errors.emailRequired")
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("leadCapture.errors.emailInvalid")
    }

    if (!formData.company.trim()) {
      newErrors.company = t("leadCapture.errors.companyRequired")
    }

    if (!formData.consent) {
      newErrors.consent = t("leadCapture.errors.consentRequired")
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    await onSubmit(formData)
    setIsSubmitting(false)
    setIsSuccess(true)
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      // Reset after animation
      setTimeout(() => {
        setIsSuccess(false)
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          message: "",
          consent: false
        })
        setErrors({})
      }, 300)
    }
  }

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    return `${mins} minute${mins !== 1 ? 's' : ''}`
  }

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">
              {t("leadCapture.thankYouTitle")}
            </h3>
            <p className="text-muted mb-6">
              {t("leadCapture.thankYouDesc")}
            </p>
            <Button onClick={handleClose} className="w-full">
              {t("leadCapture.continueDemo")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-2xl">
            {t("leadCapture.title")}
          </DialogTitle>
          <DialogDescription className="text-base">
            {t("leadCapture.description", {
              duration: formatDuration(sessionDuration),
              count: messageCount,
            })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          {/* Name */}
          <div>
            <Label htmlFor="name">
              {t("leadCapture.fields.name")} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t("leadCapture.placeholders.name")}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">
              {t("leadCapture.fields.email")} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder={t("leadCapture.placeholders.email")}
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Company */}
          <div>
            <Label htmlFor="company">
              {t("leadCapture.fields.company")} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              placeholder={t("leadCapture.placeholders.company")}
              className={errors.company ? "border-red-500" : ""}
            />
            {errors.company && (
              <p className="text-sm text-red-500 mt-1">{errors.company}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">
              {t("leadCapture.fields.phone")}{" "}
              <span className="text-muted">{t("leadCapture.fields.optional")}</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder={t("leadCapture.placeholders.phone")}
            />
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message">
              {t("leadCapture.fields.message")}{" "}
              <span className="text-muted">{t("leadCapture.fields.optional")}</span>
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder={t("leadCapture.placeholders.message")}
              rows={3}
            />
          </div>

          {/* Consent */}
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="consent"
              checked={formData.consent}
              onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <Label htmlFor="consent" className="text-sm font-normal cursor-pointer">
              {t("leadCapture.fields.consent")} <span className="text-red-500">*</span>
            </Label>
          </div>
          {errors.consent && (
            <p className="text-sm text-red-500">{errors.consent}</p>
          )}

          {/* Actions */}
          <div className="flex flex-col space-y-3 pt-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("leadCapture.submitting")}
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {t("leadCapture.getMoreInfo")}
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => window.open("https://calendly.com", "_blank")}
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t("leadCapture.bookCall")}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("leadCapture.continueDemoArrow")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
